import type { WritableDeep } from 'type-fest';

import { DocumentEditorInstantaneousContentActionType } from '../../enums/instantaneous-content-action-type.enum';
import { getDeepSection } from '../../utils/get-deep-section.util';
import { DocumentEditorActionType } from '../../enums/action-type.enum';
import { DocumentEditorTwoStepContentActionType } from '../../enums/two-step-content-action-type.enum';
import type { DocumentEditorState } from '../../types/editor-state.type';
import type { Editable } from '../../types/editable.type';
import type { DocumentEditorAction } from '../../types/actions.type';
import type { DocumentEditorContentAction } from '../../types/content-actions.type';
import type {
  DocumentEditorContentSetCustomPageBreakBeforeAction,
  DocumentEditorContentSetCustomPageBreakAfterAction,
  DocumentEditorContentSetPageBreakBeforeAction,
  DocumentEditorContentSetPageBreakAfterAction,
  DocumentEditorContentSetOrientationAction,
  DocumentEditorContentSetPaperFormatAction,
} from '../../types/content-actions.type';

import { updateCustomLayout } from '../utils/custom-layout.util';
import { applyLayoutUpdateToSectionTree } from '../utils/apply-layout-update-to-section-tree.util';
import { commitVersionedChange } from '../utils/commit-versioned-change.util';
import type { CustomLayoutUpdates } from '../utils/custom-layout.util';
import type { ReducerContext } from '../types/reducer-context.type';

const customPageBreakUpdates = (
  action:
    | DocumentEditorContentSetCustomPageBreakBeforeAction
    | DocumentEditorContentSetCustomPageBreakAfterAction,
): Partial<
  Pick<CustomLayoutUpdates, 'hasPagebreakBefore' | 'hasPagebreakAfter'>
> =>
  action.type ===
  DocumentEditorInstantaneousContentActionType.SetCustomPageBreakBefore
    ? { hasPagebreakBefore: action.payload.hasPagebreakBefore }
    : { hasPagebreakAfter: action.payload.hasPagebreakAfter };

const pageBreakUpdates = (
  action:
    | DocumentEditorContentSetPageBreakBeforeAction
    | DocumentEditorContentSetPageBreakAfterAction,
): Partial<
  Pick<CustomLayoutUpdates, 'hasPagebreakBefore' | 'hasPagebreakAfter'>
> =>
  action.type ===
  DocumentEditorInstantaneousContentActionType.SetPageBreakBefore
    ? { hasPagebreakBefore: action.payload.hasPagebreakBefore }
    : { hasPagebreakAfter: action.payload.hasPagebreakAfter };

const layoutFieldUpdates = (
  action:
    | DocumentEditorContentSetOrientationAction
    | DocumentEditorContentSetPaperFormatAction,
): Partial<Pick<CustomLayoutUpdates, 'orientation' | 'paperFormat'>> =>
  action.type === DocumentEditorInstantaneousContentActionType.SetOrientation
    ? { orientation: action.payload.orientation }
    : { paperFormat: action.payload.paperFormat };

export const layoutReducer = <E extends Editable>(
  state: DocumentEditorState<E>,
  action: DocumentEditorAction<E> | DocumentEditorContentAction,
  context: Readonly<ReducerContext<E>>,
): DocumentEditorState<E> | null => {
  switch (action.type) {
    case DocumentEditorInstantaneousContentActionType.SetCustomPageBreakBefore:
    case DocumentEditorInstantaneousContentActionType.SetCustomPageBreakAfter:
      return commitVersionedChange(state, context, (draft) =>
        updateCustomLayout({
          clonedEditable: draft.editable as WritableDeep<E>,
          insertionPointRealPath: action.payload.insertionPointRealPath,
          sectionId: action.payload.id,
          updates: customPageBreakUpdates(action),
          context,
        }),
      );

    case DocumentEditorInstantaneousContentActionType.SetPageBreakBefore:
    case DocumentEditorInstantaneousContentActionType.SetPageBreakAfter:
      return commitVersionedChange(state, context, (draft) => {
        Object.assign(
          getDeepSection(
            context.getEditable<true>(draft.editable as WritableDeep<E>)
              .virtualRoot,
            action.payload.path,
          ),
          pageBreakUpdates(action),
        );
      });

    case DocumentEditorInstantaneousContentActionType.SetOrientation:
    case DocumentEditorInstantaneousContentActionType.SetPaperFormat:
      return commitVersionedChange(state, context, (draft) => {
        applyLayoutUpdateToSectionTree({
          clonedEditable: draft.editable as WritableDeep<E>,
          section: action.payload.section,
          updates: layoutFieldUpdates(action),
          context,
        });
      });

    case DocumentEditorInstantaneousContentActionType.SetDisplayCondition:
      return commitVersionedChange(state, context, (draft) => {
        Object.assign(
          getDeepSection(
            context.getEditable<true>(draft.editable as WritableDeep<E>)
              .virtualRoot,
            action.payload.path,
          ),
          { displayCondition: action.payload.displayCondition },
        );
      });

    case DocumentEditorActionType.OpenDrawer:
    case DocumentEditorActionType.CloseDrawer:
    case DocumentEditorActionType.FoldAllSections:
    case DocumentEditorActionType.UnfoldAllSections:
    case DocumentEditorActionType.SetEditorMode:
    case DocumentEditorActionType.RequestTwoStepContentAction:
    case DocumentEditorActionType.ClearTwoStepContentAction:
    case DocumentEditorActionType.Undo:
    case DocumentEditorActionType.Redo:
    case DocumentEditorActionType.ClearHistory:
    case DocumentEditorActionType.ClearNavigationTarget:
    case DocumentEditorActionType.BumpLatestSavedVersion:
    case DocumentEditorActionType.UpdateInfo:
    case DocumentEditorActionType.UpdateAdmin:
    case DocumentEditorInstantaneousContentActionType.Duplicate:
    case DocumentEditorInstantaneousContentActionType.Move:
    case DocumentEditorInstantaneousContentActionType.SetFoldingMode:
    case DocumentEditorInstantaneousContentActionType.Delete:
    case DocumentEditorInstantaneousContentActionType.Reinitialize:
    case DocumentEditorTwoStepContentActionType.InsertBefore:
    case DocumentEditorTwoStepContentActionType.InsertAfter:
    case DocumentEditorTwoStepContentActionType.InsertInside:
    case DocumentEditorTwoStepContentActionType.InsertStart:
    case DocumentEditorTwoStepContentActionType.InsertEnd:
    case DocumentEditorTwoStepContentActionType.Update:
    default: {
      return null; // Not handled by this reducer
    }
  }
};
